// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { miniCartLineStyles } from "@flip/App/Components/MiniCart/styles";
import Notify from "@flip/App/Components/Notify/Notify";
import xProductPartNumbersStyles from "@flip/App/SharedStyle/ProductPartNumbersStyles";
import ProductAvailability from "@flip/Overrides/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@flip/Overrides/Components/ProductBrand";
import CartLineQuantity from "@flip/Overrides/Widgets/Cart/CartLineQuantity";
import { parseBoolean } from "@flip/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import React, { FC } from "react";

interface OwnProps {
    cartLine: CartLineModel;
    errorMessages?: React.ReactNode[];
    hideTippy: () => void;
    cart?: Readonly<Cart>;
    qtyOrderedChangeHandler: (cartLine: CartLineModel, qtyOrdered: number) => void;
    removeCartLineClickHandler: (cartLine: CartLineModel) => void;
}

const styles = miniCartLineStyles;

type Props = OwnProps;

const MiniCartLineCard: FC<Props> = ({
    cartLine,
    errorMessages,
    hideTippy,
    cart,
    qtyOrderedChangeHandler,
    removeCartLineClickHandler,
}) => {
    return (
        <CartLineContext.Provider value={cartLine} data-test-selector={`miniCartLine_${cartLine.id}`}>
            <StyledWrapper {...styles.cartLineWrapper} data-test-selector="cartLine">
                <StyledWrapper {...styles.imageWrapper}>
                    <Link href={cartLine.productUri} onClick={hideTippy}>
                        <LazyImage src={cartLine.smallImagePath} altText={cartLine.altText} {...styles.cartLineImage} />
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.detailsWrapper}>
                    {/* XNG-Changes: FLIP-507 */}
                    <StyledWrapper {...styles.brandWrapper}>
                        {cartLine.brand && (
                            <Link
                                href={cartLine.brand.urlSegment}
                                onClick={hideTippy}
                                {...styles.brandLink}
                                {...{ isAuthorized: parseBoolean(cartLine.properties.isAuthorizedBrand) }}
                            >
                                {cartLine.brand.name}
                            </Link>
                        )}
                    </StyledWrapper>
                    <StyledWrapper>
                        <Link href={cartLine.productUri} onClick={hideTippy} {...styles.lineDescription}>
                            {cartLine.shortDescription}
                        </Link>
                    </StyledWrapper>
                    <StyledWrapper {...styles.partNumbers?.wrapper}>
                        {cartLine.manufacturerItem && (
                            <Typography data-test-selector="erpNumber" {...xProductPartNumbersStyles.default.value}>
                                <Typography {...xProductPartNumbersStyles.default.label}>
                                    {translate("MFR#")}
                                </Typography>
                                {cartLine.manufacturerItem}
                            </Typography>
                        )}
                        {cartLine.properties.sku && (
                            <Typography
                                data-test-selector="customerProductNumber"
                                {...xProductPartNumbersStyles.default.value}
                            >
                                <Typography {...xProductPartNumbersStyles.default.label}>
                                    {translate("FPN#")}
                                </Typography>
                                {cartLine.properties.sku}
                            </Typography>
                        )}
                        {/* XNG-Changes */}
                        {cartLine.properties?.legacyManufacturer && (
                            <StyledWrapper {...styles.legacyManufacturerWrapper}>
                                <Typography {...styles.labelText}>{translate("Legacy Manufacturer")}</Typography>
                                {cartLine.properties.legacyManufacturerDetailUrl ? (
                                    <Link
                                        href={cartLine.properties.legacyManufacturerDetailUrl}
                                        onClick={hideTippy}
                                        {...styles.brandLink}
                                        {...{
                                            isAuthorized: parseBoolean(
                                                cartLine.properties.isLegacyManufacturerAnAuthorizedSupplier,
                                            ),
                                        }}
                                    >
                                        {cartLine.properties.legacyManufacturer}
                                    </Link>
                                ) : (
                                    <Typography {...xProductPartNumbersStyles.default.value}>
                                        {cartLine.properties.legacyManufacturer}
                                    </Typography>
                                )}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                    {/** XNG-Changes: FLIP-610 remove !cartLine.quoteRequired condition */}
                    <ProductAvailability
                        productId={cartLine.productId!}
                        availability={cartLine.availability!}
                        unitOfMeasure={cartLine.unitOfMeasure}
                        trackInventory={cartLine.trackInventory}
                        failedToLoadInventory={cart?.failedToGetRealTimeInventory}
                        // XNG-Changes
                        quoteRequired={cartLine.quoteRequired}
                    />
                    <StyledWrapper {...styles.qtyAndPriceWrapper}>
                        <StyledWrapper {...styles.qtyWrapper}>
                            <CartLineQuantity
                                cart={cart as Cart}
                                editable={true}
                                extendedStyles={styles.cartLineQuantityStyles}
                                showActions={true}
                                onQtyOrderedChange={(inputQty: number) => {
                                    qtyOrderedChangeHandler && qtyOrderedChangeHandler(cartLine, inputQty);
                                }}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.priceWrapper}>
                            {!cart?.cartNotPriced && (
                                <ProductPrice
                                    showLabel={false}
                                    showSavings={false}
                                    showPack={false}
                                    product={cartLine}
                                />
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                    {errorMessages && (
                        <StyledWrapper {...styles.errorMessagesWrapper}>
                            {errorMessages.map(message => (
                                <Notify
                                    type="danger"
                                    data-test-selector="cartline_errorMessage"
                                    key={`error_${cartLine.id}`}
                                >
                                    {message}
                                </Notify>
                            ))}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.closeWrapper} className="closeWrapper">
                    <Link
                        onClick={() => {
                            removeCartLineClickHandler(cartLine);
                        }}
                        data-test-selector="cartline_removeLine"
                        {...styles.removeCartLineLink}
                    />
                </StyledWrapper>
            </StyledWrapper>
        </CartLineContext.Provider>
    );
};

export default MiniCartLineCard;
